import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Building2, Truck, GraduationCap, Hotel, Users, QrCode,
  Shield, BarChart3, Headphones, Check, ChevronDown, Send,
  Zap, Globe, Lock, ArrowRight, Key, Car, Dog, Baby, Smartphone, MapPin, Package, Heart,
  Factory, Boxes, KeyRound, Code2, Download, ScanLine
} from 'lucide-react';
import { useAuth, API } from '../App';
import { t } from '../i18n';
import Header from '../components/Header';
import { toast } from 'sonner';

const defaultUseCasesMeta = [
  { id: 'fleet', icon: Truck, titleKey: 'business.uc1Title', descKey: 'business.uc1Desc', statsKey: 'business.uc1Stats', color: 'bg-blue-100', image: 'https://images.unsplash.com/photo-1772440223098-cc23f6f01209?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxmbGVldCUyMG1hbmFnZW1lbnQlMjB2ZWhpY2xlcyUyMGJ1c2luZXNzfGVufDB8fHx8MTc3NTE2OTE3MHww&ixlib=rb-4.1.0&q=85&w=600' },
  { id: 'school', icon: GraduationCap, titleKey: 'business.uc2Title', descKey: 'business.uc2Desc', statsKey: 'business.uc2Stats', color: 'bg-green-100', image: 'https://images.pexels.com/photos/10082705/pexels-photo-10082705.jpeg?auto=compress&cs=tinysrgb&w=600' },
  { id: 'hotel', icon: Hotel, titleKey: 'business.uc3Title', descKey: 'business.uc3Desc', statsKey: 'business.uc3Stats', color: 'bg-amber-100', image: 'https://images.pexels.com/photos/7942132/pexels-photo-7942132.jpeg?auto=compress&cs=tinysrgb&w=600' },
  { id: 'enterprise', icon: Building2, titleKey: 'business.uc4Title', descKey: 'business.uc4Desc', statsKey: 'business.uc4Stats', color: 'bg-purple-100', image: 'https://images.pexels.com/photos/7654403/pexels-photo-7654403.jpeg?auto=compress&cs=tinysrgb&w=600' },
];

const plans = [
  {
    id: 'starter',
    name: 'Starter',
    price: 9,
    period: '/mois',
    desc: 'Pour les petites equipes',
    features: [
      'Jusqu\'a 50 PingMes',
      '3 utilisateurs',
      'Dashboard equipe',
      'Support email',
      'QR codes personnalises',
    ],
    cta: 'Commencer',
    popular: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 99,
    period: '/mois',
    desc: 'Pour les entreprises en croissance',
    features: [
      'Jusqu\'a 500 PingMes',
      'Utilisateurs illimites',
      'Analytics avances',
      'Support prioritaire',
      'Acces API',
      'Marque personnalisee',
      'Export de donnees',
    ],
    cta: 'Choisir Pro',
    popular: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: -1,
    period: '',
    desc: 'Solutions sur mesure',
    features: [
      'PingMes illimites',
      'Utilisateurs illimites',
      'SLA garanti 99.9%',
      'Account manager dedie',
      'API illimitee',
      'SSO / SAML',
      'Deploiement on-premise',
      'Formation equipe',
    ],
    cta: 'Nous contacter',
    popular: false,
  },
];

const faqs = [
  {
    q: 'Comment fonctionne Tagora pour les entreprises ?',
    a: 'Tagora fournit des QR codes (PingMes) que vous apposez sur vos equipements, vehicules ou objets. Tout collaborateur ou tiers qui trouve un objet peut scanner le QR code et contacter votre equipe de maniere anonyme et securisee.',
  },
  {
    q: 'Puis-je personnaliser les QR codes avec notre marque ?',
    a: 'Oui. Vous pouvez personnaliser les QR codes avec votre logo et vos couleurs. Les stickers peuvent egalement etre produits avec votre charte graphique.',
  },
  {
    q: 'Y a-t-il une API pour integrer Tagora a nos outils ?',
    a: 'Absolument. Un acces API complet est disponible, avec documentation Swagger, pour integrer Tagora a votre SI, CMDB ou outil de gestion de flotte.',
  },
  {
    q: 'Quelle est la duree d\'engagement ?',
    a: 'Aucun engagement. Nos plans sont mensuels et vous pouvez annuler a tout moment. Nous proposons egalement des tarifs annuels avec 20% de reduction.',
  },
  {
    q: 'Comment est geree la confidentialite des donnees ?',
    a: 'Tagora est 100% conforme RGPD. Les communications sont anonymes (l\'identite du proprietaire n\'est jamais exposee aux personnes qui scannent), chiffrees en transit via TLS 1.3, et vous gardez le controle total via votre dashboard d\'administration.',
  },
];

const Business = () => {
  const { user } = useAuth();
  const [openFaq, setOpenFaq] = useState(null);
  
  // Build default use cases using t()
  const defaultUseCases = defaultUseCasesMeta.map(uc => ({
    ...uc, title: t(uc.titleKey), desc: t(uc.descKey), stats: t(uc.statsKey)
  }));
  
  const [useCases, setUseCases] = useState(defaultUseCases);
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    company_size: '',
    use_case: '',
    plan_interest: 'starter',
    message: '',
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    axios.get(`${API}/cms/use-cases`).then(res => {
      if (res.data.business && res.data.business.length > 0) {
        const iconMap = { Key, Car, Dog, Baby, Smartphone, MapPin, Package, Building2, Truck, GraduationCap, Hotel, Users, Shield, Heart, QrCode, Globe };
        const colors = ['bg-blue-100', 'bg-green-100', 'bg-amber-100', 'bg-purple-100'];
        setUseCases(res.data.business.map((uc, i) => ({
          id: uc.id || `uc_${i}`,
          icon: iconMap[uc.icon] || Building2,
          title: uc.title,
          desc: uc.desc,
          stats: uc.stats || '',
          color: uc.color || colors[i % 4],
          iconColor: uc.iconColor || null,
          image: uc.image || defaultUseCasesMeta[i % defaultUseCasesMeta.length]?.image,
        })));
      }
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company_name || !formData.contact_name || !formData.email || !formData.company_size || !formData.use_case) {
      toast.error(t('business.contactSend'));
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/b2b/contact`, formData);
      toast.success(t('business.contactSend') + ' !');
      setSent(true);
    } catch {
      toast.error(t('actions.send'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="business-page">
      <Header />

      {/* Hero */}
      <section className="relative px-6 sm:px-8 lg:px-12 pt-20 pb-28 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-secondary/20 rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-charcoal text-white border-2 border-charcoal rounded-full px-4 py-2 shadow-brutal text-sm font-bold mb-8">
              <Building2 className="w-4 h-4" />
              {t('business.heroTag')}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-none text-charcoal font-heading mb-6">
              {t('business.heroTitle')}<br />
              <span className="text-primary">{t('business.heroHighlight')}</span>
            </h1>
            <p className="text-lg text-charcoal/80 max-w-2xl font-body leading-relaxed mb-8">
              {t('business.heroDesc')}
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="#contact"
                data-testid="b2b-hero-cta"
                className="btn-primary inline-flex items-center gap-2"
              >
                {t('business.requestDemo')}
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted by */}
      <div className="border-y-2 border-charcoal bg-charcoal text-white py-5 px-6">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-center gap-x-12 gap-y-3">
          {[
            { icon: Shield, text: t('business.badge1') },
            { icon: Lock, text: t('business.badge2') },
            { icon: Globe, text: t('business.badge3') },
            { icon: Zap, text: t('business.badge4') },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-sm font-bold tracking-wide">
              <item.icon className="w-4 h-4 text-primary" />
              {item.text}
            </div>
          ))}
        </div>
      </div>

      {/* Use Cases */}
      <section className="px-6 sm:px-8 lg:px-12 py-14 sm:py-20" id="use-cases">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-charcoal mb-3">CAS D'USAGE</p>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-charcoal font-heading mb-8">
            Une solution pour chaque secteur
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {useCases.map((uc) => (
              <div
                key={uc.id}
                data-testid={`usecase-${uc.id}`}
                className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal overflow-hidden hover:shadow-brutal-xl hover:-translate-y-1 transition-all group"
              >
                <div className="h-28 overflow-hidden">
                  <img
                    src={uc.image}
                    alt={uc.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className={`w-8 h-8 ${uc.iconColor ? '' : uc.color} rounded-lg border-2 border-charcoal flex items-center justify-center flex-shrink-0`}
                      style={uc.iconColor ? { backgroundColor: uc.iconColor } : {}}
                    >
                      <uc.icon className="w-4 h-4 text-charcoal" />
                    </div>
                    <h3 className="font-heading font-bold text-sm leading-tight">{uc.title}</h3>
                  </div>
                  <p className="text-xs text-charcoal/70 mb-2 line-clamp-2">{uc.desc}</p>
                  <span className="inline-block text-[10px] font-bold bg-secondary/40 text-charcoal px-2 py-0.5 rounded-full border border-charcoal/10">{uc.stats}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNER PROGRAM (Tagora ID for manufacturers) */}
      <section className="px-6 sm:px-8 lg:px-12 py-24 border-t-4 border-charcoal bg-gradient-to-br from-primary/5 via-background to-background" id="partners" data-testid="partner-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 bg-primary border-2 border-charcoal rounded-full px-3 py-1 shadow-brutal text-xs font-bold mb-5">
                <Factory className="w-3.5 h-3.5" />
                PROGRAMME PARTENAIRES
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-charcoal font-heading leading-tight mb-5">
                Vous fabriquez des objets ? <span className="bg-primary px-2 -rotate-1 inline-block">Integrez Tagora ID.</span>
              </h2>
              <p className="text-charcoal/75 leading-relaxed mb-6">
                Stickers, etiquettes bagages, medailles pour animaux, porte-cles : ajoutez un Tagora ID unique sur vos produits et offrez un service "perdu-trouve" cle en main a vos clients. Inscription gratuite, cle API instantanee, generation illimitee.
              </p>
              <div className="space-y-3 mb-8">
                {[
                  { icon: KeyRound, label: 'Inscription publique avec cle API immediate' },
                  { icon: Boxes, label: 'Generation illimitee gratuite (1 a 10 000 par lot)' },
                  { icon: Download, label: 'Export CSV + ZIP de QR codes prets a imprimer' },
                  { icon: ScanLine, label: 'Premier scan -> votre client reclame le Tagora ID gratuitement' },
                  { icon: Code2, label: 'API REST documentee + dashboard partenaire' },
                ].map(({ icon: Ic, label }) => (
                  <div key={label} className="flex items-start gap-3 text-sm">
                    <div className="w-8 h-8 rounded-lg bg-primary border-2 border-charcoal flex items-center justify-center flex-shrink-0">
                      <Ic className="w-4 h-4 text-charcoal" />
                    </div>
                    <span className="text-charcoal/80 pt-1.5">{label}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-3">
                {!user && (
                  <>
                    <Link to="/register?as=partner" data-testid="partner-cta-register" className="btn-primary inline-flex items-center gap-2">
                      Devenir partenaire <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link to="/login" data-testid="partner-cta-login" className="btn-outline">J'ai deja un compte</Link>
                  </>
                )}
                {user?.role === 'partner' && (
                  <Link to="/partners/dashboard" data-testid="partner-cta-dashboard" className="btn-primary inline-flex items-center gap-2">
                    Acceder a mon espace partenaire <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
                <Link to="/label" className="btn-outline">Specifications du standard</Link>
              </div>
            </div>

            <div className="lg:col-span-7 space-y-4">
              <div className="card-brutal bg-white">
                <div className="text-xs font-bold tracking-widest uppercase text-charcoal/60 mb-2">EXEMPLE DE TAGORA ID</div>
                <div className="font-mono text-2xl sm:text-3xl font-bold text-charcoal tracking-tight mb-4 break-all">
                  TGR-7K2L-9P3M-A4B5-X9Y2
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div className="bg-background border-2 border-charcoal/10 rounded-lg p-3"><div className="text-charcoal/50 text-xs uppercase">Espace</div><div className="font-bold text-charcoal">3.7 × 10²²</div></div>
                  <div className="bg-background border-2 border-charcoal/10 rounded-lg p-3"><div className="text-charcoal/50 text-xs uppercase">Verif.</div><div className="font-bold text-charcoal">Hors-ligne</div></div>
                  <div className="bg-background border-2 border-charcoal/10 rounded-lg p-3"><div className="text-charcoal/50 text-xs uppercase">Format</div><div className="font-bold text-charcoal">Crockford</div></div>
                  <div className="bg-background border-2 border-charcoal/10 rounded-lg p-3"><div className="text-charcoal/50 text-xs uppercase">Checksum</div><div className="font-bold text-charcoal">Mod-37</div></div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { n: '01', t: 'Inscrivez-vous', d: 'Compte cree en 30 secondes, cle API immediate.' },
                  { n: '02', t: 'Generez en bulk', d: '1 a 10 000 IDs par appel via dashboard ou API REST.' },
                  { n: '03', t: 'Imprimez & vendez', d: 'ZIP de PNG ou CSV pour votre chaine de production.' },
                  { n: '04', t: 'Le client active', d: 'Premier scan = creation de compte + Tagora ID attribue.' },
                ].map((s) => (
                  <div key={s.n} className="bg-charcoal text-white rounded-2xl p-5 border-2 border-charcoal hover:border-primary transition-colors">
                    <div className="font-mono text-primary text-sm mb-2">{s.n}</div>
                    <div className="font-heading font-bold text-base mb-1">{s.t}</div>
                    <p className="text-white/70 text-xs leading-relaxed">{s.d}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="px-6 sm:px-8 lg:px-12 py-24 bg-charcoal">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-4">{t('landing.featuresTag')}</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white font-heading mb-12">
            {t('landing.featuresTitle')}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: QrCode, title: t('business.feat1Title'), desc: t('business.feat1Desc') },
              { icon: Users, title: t('business.feat2Title'), desc: t('business.feat2Desc') },
              { icon: BarChart3, title: t('business.feat3Title'), desc: t('business.feat3Desc') },
              { icon: Shield, title: t('business.feat4Title'), desc: t('business.feat4Desc') },
              { icon: Headphones, title: t('business.feat5Title'), desc: t('business.feat5Desc') },
              { icon: Globe, title: t('business.feat6Title'), desc: t('business.feat6Desc') },
            ].map((f, i) => (
              <div key={i} className="bg-white border-2 border-white rounded-2xl p-6 shadow-brutal-lg">
                <div className="bg-primary w-12 h-12 rounded-full border-2 border-charcoal flex items-center justify-center mb-4">
                  <f.icon className="w-6 h-6 text-charcoal" />
                </div>
                <h3 className="text-base font-bold text-charcoal mb-2 font-heading">{f.title}</h3>
                <p className="text-sm text-charcoal/70">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 sm:px-8 lg:px-12 py-24 bg-surface-alt">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-charcoal mb-4">{t('business.faqTag')}</p>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-charcoal font-heading mb-8">
            {t('business.faqTitle')}
          </h2>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                data-testid={`faq-${i}`}
                className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full text-left px-6 py-4 flex items-center justify-between gap-4"
                >
                  <span className="font-heading font-bold text-sm">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-4 text-sm text-charcoal/70 border-t border-charcoal/10 pt-3">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="px-6 sm:px-8 lg:px-12 py-24" id="contact">
        <div className="max-w-2xl mx-auto">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-charcoal mb-4">{t('business.contactTag')}</p>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-charcoal font-heading mb-2">
            Demandez votre demo
          </h2>
          <p className="text-charcoal/60 mb-8">Notre equipe vous recontacte sous 24 heures.</p>

          {sent ? (
            <div className="card-brutal bg-secondary/20 text-center py-12" data-testid="b2b-form-success">
              <Check className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h3 className="font-heading font-bold text-xl mb-2">Demande envoyee !</h3>
              <p className="text-charcoal/70">Notre equipe commerciale vous contactera dans les 24 prochaines heures.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="card-brutal space-y-5" data-testid="b2b-contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold mb-1.5">Nom de l'entreprise *</label>
                  <input
                    className="input-brutal w-full"
                    placeholder={t('business.contactCompany')}
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    required
                    data-testid="b2b-company-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold mb-1.5">Votre nom *</label>
                  <input
                    className="input-brutal w-full"
                    placeholder={t('business.contactName')}
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    required
                    data-testid="b2b-contact-name"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold mb-1.5">Email professionnel *</label>
                  <input
                    className="input-brutal w-full"
                    type="email"
                    placeholder={t('business.contactEmail')}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="b2b-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold mb-1.5">Telephone</label>
                  <input
                    className="input-brutal w-full"
                    placeholder={t('business.contactPhone')}
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    data-testid="b2b-phone"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold mb-1.5">Taille de l'entreprise *</label>
                  <select
                    className="input-brutal w-full"
                    value={formData.company_size}
                    onChange={(e) => setFormData({ ...formData, company_size: e.target.value })}
                    required
                    data-testid="b2b-company-size"
                  >
                    <option value="">Selectionnez</option>
                    <option value="1-10">1-10 employes</option>
                    <option value="11-50">11-50 employes</option>
                    <option value="51-200">51-200 employes</option>
                    <option value="200+">200+ employes</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold mb-1.5">Cas d'usage *</label>
                  <select
                    className="input-brutal w-full"
                    value={formData.use_case}
                    onChange={(e) => setFormData({ ...formData, use_case: e.target.value })}
                    required
                    data-testid="b2b-use-case"
                  >
                    <option value="">Selectionnez</option>
                    <option value="fleet">{t('categories.car')}</option>
                    <option value="school">{t('categories.child')}</option>
                    <option value="hotel">{t('categories.other')}</option>
                    <option value="enterprise">Entreprise</option>
                    <option value="other">{t('categories.other')}</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold mb-1.5">Message (optionnel)</label>
                <textarea
                  className="input-brutal w-full"
                  rows={3}
                  placeholder={t('business.contactMessage')}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  data-testid="b2b-message"
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="b2b-submit-btn"
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {sending ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-charcoal border-t-transparent" />
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Envoyer ma demande
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Footer CTA */}
      <section className="px-6 sm:px-8 lg:px-12 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="card-brutal bg-charcoal text-center p-12">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white font-heading mb-4">
              Pret a securiser vos actifs ?
            </h2>
            <p className="text-white/70 mb-8">
              {t('landing.ctaDesc')}
            </p>
            <a href="#contact" className="btn-primary inline-flex items-center gap-2 text-lg">
              {t('business.requestDemoFree')}
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t-2 border-charcoal px-6 sm:px-8 lg:px-12 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="bg-primary w-10 h-10 rounded-full border-2 border-charcoal flex items-center justify-center">
                <span className="font-heading font-bold text-charcoal">T</span>
              </div>
              <span className="font-heading font-bold text-xl text-charcoal">Tagora</span>
            </div>
            <div className="flex gap-8 text-sm text-charcoal/70">
              <Link to="/shop" className="hover:text-charcoal transition-colors">Boutique</Link>
              <Link to="/business" className="hover:text-charcoal transition-colors font-bold text-charcoal">Entreprises</Link>
              <Link to="/legal" className="hover:text-charcoal transition-colors">Mentions legales</Link>
            </div>
            <p className="text-sm text-charcoal/50">2026 Tagora. Tous droits reserves.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Business;
